/**
 * KpiStrip — tira de KPIs clicables que setean el filtro de estado del módulo
 * GPS (compartida por GpsMonitoreo y GpsDispositivos).
 *
 * Cada item mapea a un valor de filtro (`filter`); clicar un KPI activo lo
 * quita (eso lo resuelve el caller en `onFilter`). `online`/`alarma` son
 * filtros derivados; el resto coincide 1:1 con d.estado.
 *
 * Layout responsive:
 *  - <sm: fila horizontal de chips compactos con overflow-x-auto (el fade del
 *    borde derecho hace de affordance de scroll).
 *  - sm+: grilla con divisores, misma densidad que el resto de las vistas.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils/cn';

// Clases estáticas por cantidad de items (Tailwind no genera clases dinámicas).
const GRID_COLS = {
  6: 'sm:grid-cols-3 lg:grid-cols-6',
  7: 'sm:grid-cols-4 lg:grid-cols-7',
};

export function KpiStrip({ items, value, onFilter }) {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-xl border border-border bg-bg shadow-soft overflow-hidden">
      <div className={cn('flex overflow-x-auto sm:grid divide-x sm:divide-y-0 divide-border', GRID_COLS[items.length] ?? GRID_COLS[6])}>
        {items.map((it) => {
          const active = value === it.filter;
          return (
            <button
              key={it.label}
              type="button"
              onClick={() => onFilter(it.filter)}
              aria-pressed={active}
              title={active ? t('Quitar filtro') : t('Filtrar por {{label}}', { label: it.label })}
              className={cn(
                'shrink-0 sm:shrink px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-2.5 sm:gap-3 sm:min-w-0 text-left transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
                active ? 'bg-brand-50 dark:bg-brand-500/10' : 'hover:bg-bg-muted/60',
              )}
            >
              {it.dot ? (
                <span
                  className={cn('w-2.5 h-2.5 rounded-full shrink-0', it.dot)}
                  style={{ boxShadow: '0 0 0 3px rgb(var(--bg))' }}
                  aria-hidden
                />
              ) : (
                <span
                  className={cn(
                    'w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0',
                    it.tone === 'error'   ? 'bg-status-error-soft text-status-error' :
                    it.tone === 'warning' ? 'bg-status-warning-soft text-status-warning' :
                    it.tone === 'success' ? 'bg-status-success-soft text-status-success' :
                    it.tone === 'info'    ? 'bg-status-info-soft text-status-info' :
                                            'bg-bg-muted text-fg-muted',
                  )}
                >
                  {it.icon}
                </span>
              )}
              <div className="sm:min-w-0">
                {/* En el chip mobile el label dimensiona el ancho (nowrap); en la
                    grilla puede partir en 2 líneas en vez de truncar (break-words:
                    una palabra sola más ancha que la celda se quebraría en vez de
                    quedar recortada sin elipsis). */}
                <div className={cn(
                  'text-[10px] uppercase tracking-wide font-medium leading-tight whitespace-nowrap sm:whitespace-normal sm:break-words sm:line-clamp-2',
                  active ? 'text-brand-700' : 'text-fg-muted',
                )}>
                  {it.label}
                </div>
                <div className="text-base sm:text-lg font-semibold tabular-nums text-fg leading-tight">{it.value}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div
        className="sm:hidden pointer-events-none absolute inset-y-0 right-0 w-6 rounded-r-xl bg-gradient-to-l from-bg to-transparent"
        aria-hidden
      />
    </div>
  );
}

export default KpiStrip;
